import type { TimelineItem } from "@/models/legislation/Timeline";
import type { InboundChange } from "@/models/legislation/metadata/ConsolidationMetadata";
import {
  DOCUMENT_TYPE_ID_AENDRINGSBEKENDTGØRELSE,
  DOCUMENT_TYPE_ID_BEKENDTGØRELSE,
  DOCUMENT_TYPE_ID_LOV,
  DOCUMENT_TYPE_ID_LOVBEKENDTGØRELSE,
  isConsolidationAnchorType,
} from "@/utils/legislationDocumentTypeUtils";
import { isTimelineAmendmentAtPosition } from "@/lib/timelineUniverseParams";

export type TimelineDocumentTypeKind =
  | "hovedlov"
  | "amendment"
  | "ændringsbekendtgørelse"
  | "lovbekendtgørelse"
  | "bekendtgørelse";

export type TimelineSelection = {
  kind: "silver" | "consolidation";
  accessionNumber: string;
  /** Flat index in timeline_gold when the same accession appears more than once. */
  flatTimelineIndex?: number;
};

/** Law/LBK strip legend — no standalone Bekendtgørelse (type 60 has its own timelines). */
export const LAW_DOCUMENT_TYPE_KINDS: TimelineDocumentTypeKind[] = [
  "hovedlov",
  "amendment",
  "lovbekendtgørelse",
];

/** BEK strip legend — Bekendtgørelse + Ændringsbekendtgørelse only. */
export const BEK_DOCUMENT_TYPE_KINDS: TimelineDocumentTypeKind[] = [
  "bekendtgørelse",
  "ændringsbekendtgørelse",
];

export const TIMELINE_CIRCLE_FILL: Record<TimelineDocumentTypeKind, string> = {
  hovedlov: "bg-success",
  amendment: "bg-warning",
  ændringsbekendtgørelse: "bg-warning",
  lovbekendtgørelse: "bg-primary",
  bekendtgørelse: "bg-primary",
};

export const TIMELINE_DIAMOND_FILL: Record<
  InboundChange["status"],
  string
> = {
  APPROVED: "bg-success",
  NEED_REVIEW: "bg-warning",
};

export const TIMELINE_SELECTION_RING = "ring-2 ring-foreground ring-offset-2 ring-offset-background";
export const TIMELINE_COMPARE_RING = "ring-2 ring-amber-500 ring-offset-2 ring-offset-background";

export const TIMELINE_OUT_OF_ORDER_CIRCLE =
  "border-2 border-dashed border-muted-foreground/60 opacity-80";

export const TIMELINE_OUT_OF_ORDER_LABEL = "Ikke i rækkefølge";

export const CONSOLIDATION_VERIFIED_LABEL = "Konsolideret (verificeret)";
export const CONSOLIDATION_DRAFT_LABEL = "Konsolideret (udkast)";

export const AMENDMENT_DOCUMENT_TYPE_DETAIL =
  "(Kan både være ændringslov, hovedlov som ændrer andre love, eller ikræfttrædelses-bekendtgørelse)";

/** BEK-timeline amendment copy (type 80) — a bekendtgørelse that amends the forskrift. */
export const BEKENDTGØRELSE_AMENDMENT_LABEL = "Ændringsbekendtgørelse";
export const BEKENDTGØRELSE_AMENDMENT_DETAIL =
  "(Bekendtgørelse der ændrer forskriften)";

/** Readable tooltip copy for the toolbar indicator (not legend parentheticals). */
export const AMENDMENT_TOOLTIP_LINE =
  "En lov eller bekendtgørelse der ændrer andre love — fx en ændringslov, en hovedlov med ændringsbestemmelser, eller en ikræfttrædelsesbekendtgørelse.";

export const BEKENDTGØRELSE_AMENDMENT_TOOLTIP_LINE =
  "Bekendtgørelse der ændrer forskriften.";

export const OUT_OF_ORDER_TOOLTIP_LINE =
  "Placeret uden for kronologisk rækkefølge på tidslinjen.";

export const CONSOLIDATION_VERIFIED_TOOLTIP_LINE =
  "Godkendt konsolideret lovtekst, samlet ud fra gældende ændringslove.";

export const CONSOLIDATION_DRAFT_TOOLTIP_LINE =
  "Udkast til konsolideret lovtekst — endnu ikke verificeret.";

export type CurrentTimelineDocumentDisplay = {
  shape: "circle" | "diamond";
  fillClass: string;
  label: string;
  /** Extra tooltip lines (never repeat the visible label). */
  tooltipLines?: string[];
  isOutOfOrder?: boolean;
};

export type ResolveCurrentTimelineDocumentDisplayParams = {
  flatTimelineItems: ReadonlyArray<TimelineItem>;
  activeFlatTimelineIndex?: number | null;
  timelineSelection?: TimelineSelection | null;
  isConsolidatedRender?: boolean;
  isConsolidationNotFound?: boolean;
  /**
   * Live review status of the selected consolidation (change law), from the
   * per-timeline window metadata — the same source the timeline diamonds use.
   * `APPROVED` shows the verified badge; anything else (or unknown) shows draft.
   */
  consolidationStatus?: InboundChange["status"] | null;
  fallbackDocumentTypeId?: string | number | null;
  /** When false, skip metadata fallback until the timeline fetch has settled. */
  isTimelineReady?: boolean;
};

const resolveSilverTimelineDocumentDisplay = (
  documentTypeId: number,
  flatTimelineIndex: number,
  isOutOfOrder: boolean
): CurrentTimelineDocumentDisplay => {
  const kind = getTimelineDocumentTypeKind(documentTypeId, flatTimelineIndex);
  const tooltipLines: string[] = [];

  if (kind === "amendment") {
    tooltipLines.push(AMENDMENT_TOOLTIP_LINE);
  }
  if (kind === "ændringsbekendtgørelse") {
    tooltipLines.push(BEKENDTGØRELSE_AMENDMENT_TOOLTIP_LINE);
  }
  if (isOutOfOrder) {
    tooltipLines.push(OUT_OF_ORDER_TOOLTIP_LINE);
  }

  return {
    shape: "circle",
    fillClass: isOutOfOrder
      ? TIMELINE_OUT_OF_ORDER_CIRCLE
      : TIMELINE_CIRCLE_FILL[kind],
    label: getDocumentTypeLabel(kind),
    ...(tooltipLines.length > 0 ? { tooltipLines } : {}),
    ...(isOutOfOrder ? { isOutOfOrder: true } : {}),
  };
};

export const resolveCurrentTimelineDocumentDisplay = (
  params: ResolveCurrentTimelineDocumentDisplayParams
): CurrentTimelineDocumentDisplay | null => {
  const {
    flatTimelineItems,
    activeFlatTimelineIndex,
    isConsolidatedRender = false,
    isConsolidationNotFound = false,
    consolidationStatus,
    fallbackDocumentTypeId,
    isTimelineReady = true,
  } = params;

  if (isConsolidatedRender && !isConsolidationNotFound) {
    const isVerified = consolidationStatus === "APPROVED";
    return {
      shape: "diamond",
      fillClass: isVerified
        ? TIMELINE_DIAMOND_FILL.APPROVED
        : TIMELINE_DIAMOND_FILL.NEED_REVIEW,
      label: isVerified
        ? CONSOLIDATION_VERIFIED_LABEL
        : CONSOLIDATION_DRAFT_LABEL,
      tooltipLines: [
        isVerified
          ? CONSOLIDATION_VERIFIED_TOOLTIP_LINE
          : CONSOLIDATION_DRAFT_TOOLTIP_LINE,
      ],
    };
  }

  if (
    activeFlatTimelineIndex != null &&
    flatTimelineItems[activeFlatTimelineIndex]
  ) {
    const item = flatTimelineItems[activeFlatTimelineIndex];
    return resolveSilverTimelineDocumentDisplay(
      item.documentTypeId,
      activeFlatTimelineIndex,
      item.isOutOfOrder
    );
  }

  if (
    isTimelineReady &&
    fallbackDocumentTypeId != null &&
    flatTimelineItems.length === 0 &&
    activeFlatTimelineIndex == null
  ) {
    return resolveSilverTimelineDocumentDisplay(
      Number(fallbackDocumentTypeId),
      0,
      false
    );
  }

  return null;
};

export const getTimelineDocumentTypeKind = (
  documentTypeId: number,
  flatTimelineIndex: number
): TimelineDocumentTypeKind => {
  if (Number(documentTypeId) === DOCUMENT_TYPE_ID_LOVBEKENDTGØRELSE) {
    return "lovbekendtgørelse";
  }
  if (Number(documentTypeId) === DOCUMENT_TYPE_ID_BEKENDTGØRELSE) {
    return "bekendtgørelse";
  }
  if (Number(documentTypeId) === DOCUMENT_TYPE_ID_AENDRINGSBEKENDTGØRELSE) {
    return "ændringsbekendtgørelse";
  }
  if (isTimelineAmendmentAtPosition(documentTypeId, flatTimelineIndex)) {
    return "amendment";
  }
  if (Number(documentTypeId) === DOCUMENT_TYPE_ID_LOV && flatTimelineIndex === 0) {
    return "hovedlov";
  }
  return "amendment";
};

export const getCircleFillClass = (
  documentTypeId: number,
  flatTimelineIndex: number
): string =>
  TIMELINE_CIRCLE_FILL[
    getTimelineDocumentTypeKind(documentTypeId, flatTimelineIndex)
  ];

/**
 * A bekendtgørelse timeline is rooted at a Bekendtgørelse (type 60) and only
 * contains BEK (60) and Ændringsbekendtgørelse (80) nodes — never a hovedlov,
 * lovbekendtgørelse, or out-of-order changelaw. Used to tailor the legend.
 */
export const isBekendtgoerelseTimeline = (root: TimelineItem): boolean =>
  Number(root.documentTypeId) === DOCUMENT_TYPE_ID_BEKENDTGØRELSE;

export const getDiamondFillClass = (
  status: InboundChange["status"]
): string => TIMELINE_DIAMOND_FILL[status];

export const buildFlatTimelineIndexMap = (
  items: TimelineItem[]
): Map<string, number> => {
  const map = new Map<string, number>();
  items.forEach((item, index) => {
    if (item.accessionNumber) {
      map.set(item.accessionNumber, index);
    }
  });
  return map;
};

/** All flat indices for an accession (supports duplicate / out-of-order nodes). */
export const findFlatTimelineIndicesForAccession = (
  items: TimelineItem[],
  accessionNumber: string
): number[] => {
  const indices: number[] = [];
  items.forEach((item, index) => {
    if (item.accessionNumber === accessionNumber) indices.push(index);
  });
  return indices;
};

/** Default instance when URL does not pin timeline_at (prefer in-order over out-of-order). */
export const resolveDefaultFlatTimelineIndex = (
  items: TimelineItem[],
  accessionNumber: string | undefined
): number | undefined => {
  const trimmed = accessionNumber?.trim();
  if (!trimmed || !items.length) return undefined;

  const indices = findFlatTimelineIndicesForAccession(items, trimmed);
  if (indices.length === 0) return undefined;
  if (indices.length === 1) return indices[0];

  const inOrder = indices.find((index) => !items[index]?.isOutOfOrder);
  return inOrder ?? indices[0];
};

const matchesTimelineInstance = (
  selection: TimelineSelection | null | undefined,
  accessionNumber: string | null | undefined,
  flatTimelineIndex: number | undefined
): boolean => {
  if (!selection?.accessionNumber || !accessionNumber) return false;
  if (selection.accessionNumber !== accessionNumber) return false;
  if (
    selection.flatTimelineIndex != null &&
    flatTimelineIndex != null
  ) {
    return selection.flatTimelineIndex === flatTimelineIndex;
  }
  if (selection.flatTimelineIndex != null || flatTimelineIndex != null) {
    return false;
  }
  return true;
};

/**
 * Flat index of the canonical (in-order) node that owns an inbound consolidation
 * entry within the anchor window (excludes out-of-order duplicates).
 *
 * The node is resolved by ACCESSION rather than by counting to
 * `inbound.timelineIndex`. The viewer may reorder the timeline for display (e.g.
 * normalized BEK amendments are sorted by signature date and lifted onto the
 * spine), so the flat order can diverge from the backend order that
 * `timelineIndex` is derived from. Matching by accession keeps diamonds bound to
 * the correct document regardless of display order, while still de-duplicating
 * out-of-order copies (the first in-order occurrence wins).
 */
export const findPrimaryFlatIndexForInbound = (
  flatTimeline: TimelineItem[],
  anchorLawAccession: string | undefined,
  inbound: InboundChange
): number | null => {
  const anchor = anchorLawAccession?.trim();
  if (!anchor) return null;

  const anchorIdx = flatTimeline.findIndex(
    (item) => item.accessionNumber === anchor
  );
  if (anchorIdx < 0) return null;

  for (let i = anchorIdx + 1; i < flatTimeline.length; i++) {
    const node = flatTimeline[i];
    if (isConsolidationAnchorType(node.documentTypeId)) {
      break;
    }
    if (
      node.accessionNumber === inbound.changelawAccession &&
      !node.isOutOfOrder
    ) {
      return i;
    }
  }

  return null;
};

/**
 * The window anchor (nearest preceding consolidation anchor: LBK 30 or BEK 60)
 * that owns a timeline node. Returns undefined for nodes before the first anchor
 * on the timeline.
 */
export const resolveWindowAnchorForFlatIndex = (
  flatTimeline: TimelineItem[],
  flatTimelineIndex: number
): string | undefined => {
  for (let i = flatTimelineIndex; i >= 0; i--) {
    if (isConsolidationAnchorType(flatTimeline[i]?.documentTypeId)) {
      return flatTimeline[i]?.accessionNumber || undefined;
    }
  }
  return undefined;
};

/**
 * Inbound consolidation for a timeline node, resolving each node against its own
 * LBK window. `inboundByAnchor` maps a window anchor accession to that window's
 * inbound changes (from the per-timeline window-metadata endpoint).
 */
export const resolveInboundForTimelineNodeAcrossWindows = (
  flatTimeline: TimelineItem[],
  flatTimelineIndex: number,
  inboundByAnchor: ReadonlyMap<string, ReadonlyArray<InboundChange>> | undefined
): InboundChange | undefined => {
  if (!inboundByAnchor?.size) return undefined;

  const anchor = resolveWindowAnchorForFlatIndex(flatTimeline, flatTimelineIndex);
  if (!anchor) return undefined;

  return resolveInboundForTimelineNode(
    flatTimeline,
    flatTimelineIndex,
    inboundByAnchor.get(anchor),
    anchor
  );
};

/** Inbound consolidation for a specific timeline node (not shared across duplicate accessions). */
export const resolveInboundForTimelineNode = (
  flatTimeline: TimelineItem[],
  flatTimelineIndex: number,
  inboundChanges: ReadonlyArray<InboundChange> | undefined,
  anchorLawAccession: string | undefined
): InboundChange | undefined => {
  const node = flatTimeline[flatTimelineIndex];
  if (!node?.accessionNumber || !inboundChanges?.length) return undefined;

  const inbound = inboundChanges.find(
    (entry) => entry.changelawAccession === node.accessionNumber
  );
  if (!inbound) return undefined;

  if (node.isOutOfOrder) return undefined;

  const primaryIndex = findPrimaryFlatIndexForInbound(
    flatTimeline,
    anchorLawAccession,
    inbound
  );
  if (primaryIndex != null && primaryIndex !== flatTimelineIndex) {
    return undefined;
  }

  return inbound;
};

export const resolveConsolidationAsOfForInbound = (
  inbound: InboundChange
): string | null =>
  inbound.status === "APPROVED" ? inbound.approvedAt ?? null : null;

export const isCircleSelected = (
  selection: TimelineSelection | null | undefined,
  accessionNumber: string | null | undefined,
  flatTimelineIndex?: number
): boolean =>
  Boolean(
    selection?.kind === "silver" &&
      matchesTimelineInstance(selection, accessionNumber, flatTimelineIndex)
  );

export const isDiamondSelected = (
  selection: TimelineSelection | null | undefined,
  accessionNumber: string | null | undefined,
  flatTimelineIndex?: number
): boolean =>
  Boolean(
    selection?.kind === "consolidation" &&
      matchesTimelineInstance(selection, accessionNumber, flatTimelineIndex)
  );

export const getDocumentTypeLabel = (
  kind: TimelineDocumentTypeKind
): string => {
  switch (kind) {
    case "hovedlov":
      return "Hovedlov";
    case "amendment":
      return "Lov der ændrer";
    case "ændringsbekendtgørelse":
      return BEKENDTGØRELSE_AMENDMENT_LABEL;
    case "lovbekendtgørelse":
      return "Lovbekendtgørelse";
    case "bekendtgørelse":
      return "Bekendtgørelse";
  }
};

export type DocumentTypeTooltipContent = {
  title?: string;
  lines: string[];
};

/** Whether the toolbar badge should show a hover tooltip. */
export const shouldShowDocumentTypeTooltip = (
  display: CurrentTimelineDocumentDisplay,
  labelHidden: boolean
): boolean => {
  if (labelHidden) return true;
  return Boolean(display.tooltipLines?.length);
};

/** Structured tooltip body — omits redundant label repeat on desktop. */
export const getDocumentTypeTooltipContent = (
  display: CurrentTimelineDocumentDisplay,
  labelHidden: boolean
): DocumentTypeTooltipContent | null => {
  if (!shouldShowDocumentTypeTooltip(display, labelHidden)) {
    return null;
  }

  const lines = display.tooltipLines ?? [];

  if (labelHidden) {
    return {
      title: display.label,
      lines,
    };
  }

  return { lines };
};
